/**
 * Modal for moving an action into a group — either a personal action (into its
 * first group) or a group action (to another group).
 *
 * Only groups the user can contribute to (Contributor/Admin) and that differ
 * from the action's current group are offered as targets. The backend
 * re-derives the action's reference ID from the target group.
 */

import { useEffect, useMemo, useState } from 'react';
import { usePostHog } from 'src/lib/posthog';
import { ArrowsLeftRightIcon } from '@phosphor-icons/react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Dropdown from '@/components/ui/Dropdown';
import { Action, Group } from '../types';
import { canCreateGroupAction } from '../permissions';

interface MoveActionModalProps {
  isOpen: boolean;
  action: Action | null;
  /** All groups in the tenant; filtered here to valid move targets. */
  groups: Group[];
  /**
   * The action's current group id, when the caller knows it (e.g. the group
   * hub). Used to exclude exactly that group from the targets. When omitted,
   * we fall back to matching on short_reference.
   */
  currentGroupId?: string | null;
  onClose: () => void;
  onSubmit: (targetGroupId: string) => Promise<void>;
  isSubmitting?: boolean;
}

export function MoveActionModal({
  isOpen,
  action,
  groups,
  currentGroupId,
  onClose,
  onSubmit,
  isSubmitting
}: MoveActionModalProps) {
  const posthog = usePostHog();
  const [targetGroupId, setTargetGroupId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Valid targets: groups the user can contribute to, excluding the action's
  // current group. Prefer excluding by id (exact); fall back to short_reference
  // when the current group id isn't known — but short_references aren't unique,
  // so id matching is what lets a same-prefix sibling group remain a target.
  const targetGroups = useMemo(() => {
    if (!action) return [];
    return groups
      .filter((g) => canCreateGroupAction(g))
      .filter((g) => (currentGroupId ? g.id !== currentGroupId : g.short_reference !== action.short_reference))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groups, action, currentGroupId]);

  const options = useMemo(() => targetGroups.map((g) => ({ label: g.name, value: g.id })), [targetGroups]);

  // Personal actions (no group) join a group; group actions leave their current
  // group. The copy differs slightly between the two.
  const isInGroup = !!action && action.short_reference !== 'PERSONAL' && action.short_reference !== 'PAM';

  // Clear the selection whenever the modal opens or closes, or the action
  // changes. Resetting on close too means the next open starts from a clean
  // state on its first render — otherwise the stale id lingers in the Dropdown
  // (which keeps its own selection and only re-syncs on a *defined* initialValue),
  // and if that group is no longer an offered target it renders as a raw id.
  useEffect(() => {
    setTargetGroupId(null);
    setError(null);
  }, [isOpen, action?.id]);

  if (!isOpen || !action) return null;

  const handleSubmit = async () => {
    if (!targetGroupId) {
      setError('Select a group to move this action to.');
      return;
    }
    setError(null);
    try {
      await onSubmit(targetGroupId);
      posthog?.capture('actions_moved', { actionId: action.id, targetGroupId });
    } catch {
      // Errors are surfaced by the caller (toast); keep the modal open for retry.
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={isInGroup ? 'Move action to another group' : 'Move action to a group'}
    >
      <div className="flex flex-col gap-4 p-1">
        <p className="text-sm text-content-subtle">
          Moving <span className="font-medium text-content-default">{action.reference_id}</span> gives the new group's
          members access and assigns it a new reference ID from that group.
          {isInGroup ? ' It will no longer appear under its current group.' : ''}
        </p>

        {options.length === 0 ? (
          <p className="text-sm text-content-subtle">
            There are no other groups you can move this action into. You need to be a Contributor or Admin in the target
            group.
          </p>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destination group</label>
            <Dropdown
              options={options}
              // '' (not undefined) so clearing the selection re-syncs the
              // Dropdown back to the placeholder — it ignores an undefined
              // initialValue and would otherwise keep a stale selection.
              initialValue={targetGroupId ?? ''}
              onChange={(value) => {
                setTargetGroupId(value as string);
                setError(null);
              }}
              width="w-full"
              placeholder="Select a group..."
            />
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" text="Cancel" onClick={onClose} disabled={isSubmitting} />
          <Button
            variant="primary"
            icon={ArrowsLeftRightIcon}
            text="Move action"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting || options.length === 0 || !targetGroupId}
          />
        </div>
      </div>
    </Modal>
  );
}

export default MoveActionModal;
